import { Component, h, Host, State, Listen, Prop } from '@stencil/core'
import { CloudflareImageOptions } from '@rdss/core'
import {
    GlobalSearchSuggestions,
    SuggestionsData,
    SuggestionsSection,
    NoResults,
    SimpleSuggestions,
    Divider,
    ProductSuggestions,
} from '@rdss/elements'

/**
 * Component to render global search suggestions.
 * @class WfGlobalSearchSuggestions
 * @implements {GlobalSearchSuggestions<SuggestionsData>}
 *
 * @part suggestions-section - The container for the different suggestion type areas.
 * Required for the vx-global-search to control keyboard navigation.
 */
@Component({
    tag: 'wf-global-search-suggestions',
    styleUrl: 'wf-global-search-suggestions.scss',
    scoped: true,
})
// eslint-disable-next-line require-jsdoc
export class WfGlobalSearchSuggestions implements GlobalSearchSuggestions<SuggestionsData> {
    @State() suggestions: SuggestionsData = {} as SuggestionsData

    @Prop() snippets = {
        noResultsMessage: 'Keine Ergebnisse gefunden',
        productSuggestionsTitle: 'Produktvorschläge zu deiner Suche',
        categorySuggestionsTitle: 'Kategorien',
        emotionsSuggestionsTitle: 'Einkaufswelten',
        manufacturerSuggestionsTitle: 'Hersteller',
    }

    /**
     * Event listener for global search changes.
     */
    @Listen('globalSearchChanged')
    handleSearchChanged({ detail }: CustomEvent<SuggestionsData>) {
        this.suggestions = { ...this.suggestions, ...detail }
    }

    private productImageOptions: CloudflareImageOptions = { format: 'avif', height: 72 }

    /**
     * @inheritdoc
     */
    render({
        categorySuggestions,
        emotionSuggestions,
        manufacturerSuggestions,
        products,
        hasResults,
        term,
    } = this.suggestions) {
        return (
            <Host>
                {!hasResults && term?.length && <NoResults message={this.snippets.noResultsMessage}/>}

                {hasResults && [
                    <SuggestionsSection>
                        <SimpleSuggestions
                            title={this.snippets.categorySuggestionsTitle}
                            suggestions={categorySuggestions}/>

                        <SimpleSuggestions
                            title={this.snippets.emotionsSuggestionsTitle}
                            suggestions={emotionSuggestions}/>

                        <SimpleSuggestions
                            title={this.snippets.manufacturerSuggestionsTitle}
                            suggestions={manufacturerSuggestions}/>
                    </SuggestionsSection>,

                    <Divider/>,

                    <SuggestionsSection>
                        <ProductSuggestions
                            title={this.snippets.productSuggestionsTitle}
                            suggestions={products}
                            imageOptions={this.productImageOptions}/>
                    </SuggestionsSection>,
                ]}
            </Host>
        )
    }
}
